import { Stack, Typography } from '@mui/material';
import { ControlTimeInput } from '@schooly/components/form-time-input';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import { TimetableStructurePeriodForm } from '../../../../../context/timetableStructure/WithTimetableStructure';
import {
  getDurationFromMinutes,
  getDurationLabel,
  getMinutesDuration,
  timeToMinutes,
} from '../../utils';

interface TimetableStructurePeriodTimeInputsProps {
  validateStartTime: (startTime: string) => boolean | string;
  validateEndTime: (endTime: string) => boolean | string;
}

export const TimetableStructurePeriodTimeInputs: FC<TimetableStructurePeriodTimeInputsProps> = ({
  validateStartTime,
  validateEndTime,
}) => {
  const { formatMessage } = useIntl();
  const { control, formState, watch } = useFormContext<TimetableStructurePeriodForm>();

  const errors = formState.errors;
  const hasTimeError = Boolean(errors.start_time || errors.end_time);

  const startTime = watch('start_time');
  const endTime = watch('end_time');

  const durationLabel = useMemo(() => {
    if (!startTime || !endTime) {
      return '';
    }

    if (timeToMinutes(endTime) < timeToMinutes(startTime)) {
      return '';
    }

    const durationInMinutes = getMinutesDuration(startTime, endTime);

    let duration = { hours: 0, minutes: 0 };
    if (durationInMinutes > 0) {
      duration = getDurationFromMinutes(durationInMinutes);
    }

    return getDurationLabel(duration, {
      hours: formatMessage({ id: 'school-timetableStructure-Hours' }),
      minutes: formatMessage({ id: 'school-timetableStructure-Minutes' }),
    }).toUpperCase();
  }, [startTime, endTime, formatMessage]);

  return (
    <Stack direction="row" alignItems="flex-start" gap={1}>
      <ControlTimeInput
        control={control}
        name="start_time"
        label={formatMessage({ id: 'school-timetableStructure-Start' })}
        rules={{ required: true, validate: validateStartTime }}
      />
      <Typography variant="h3" color="primary.main" pt={1.5}>
        <FormattedMessage id="school-timetableStructure-To" />
      </Typography>
      <ControlTimeInput
        control={control}
        name="end_time"
        label={formatMessage({ id: 'school-timetableStructure-End' })}
        rules={{ required: true, validate: validateEndTime }}
      />
      {durationLabel && (
        <Typography variant="h3" color={hasTimeError ? 'error.main' : 'text.secondary'} pt={1.5}>
          {durationLabel}
        </Typography>
      )}
    </Stack>
  );
};
